import React, { useState, useEffect } from 'react';
import { Routes, Route, NavLink, useNavigate, Navigate } from 'react-router-dom';
import { Menu, X, ArrowRight } from 'lucide-react';
import PageDomov from './PageDomov';
import PagePravidla from './PagePravidla';
import PageVysledky from './PageVysledky';
import PageGaleria from './PageGaleria';
import PageBuchta from './PageBuchta';
import PageRegistracia from './PageRegistracia';
import API_URL from '../../config';

const navItems = [
    { path: '/', label: 'Domov', exact: true },
    { path: '/pravidla', label: 'Pravidlá' },
    { path: '/vysledky', label: 'Výsledky' },
    { path: '/galeria', label: 'Galéria' },
    { path: '/buchta', label: 'Vodácka Buchta', special: true },
];

export default function Website() {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [config, setConfig] = useState({});
    const navigate = useNavigate();

    useEffect(() => {
        fetch(`${API_URL}/web-config`)
            .then(r => r.json())
            .then(setConfig)
            .catch(() => { });
    }, []);

    const handleNav = (path) => {
        setMobileMenuOpen(false);
        navigate(path);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
        <div className="min-h-screen bg-blue-950 font-sans selection:bg-yellow-400 selection:text-blue-900 flex flex-col" style={{ fontFamily: 'Inter, sans-serif' }}>

            {/* Navigácia */}
            <nav className="fixed w-full z-50 top-0 bg-blue-900/90 backdrop-blur-lg border-b border-white/10 shadow-lg">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 h-20 flex items-center justify-between">

                    {/* Logo */}
                    <div className="flex items-center gap-3 cursor-pointer group shrink-0" onClick={() => handleNav('/')}>
                        <div className="w-10 h-10 md:w-12 md:h-12 rounded-full border-2 border-yellow-400 p-0.5 bg-white group-hover:scale-105 transition-transform shrink-0">
                            <img src="https://klubmalydunaj.sk/wp-content/uploads/2020/02/logo-3.png" alt="Logo" className="w-full h-full rounded-full" />
                        </div>
                        <span className="text-white font-black uppercase tracking-widest block text-[10px] md:text-sm leading-tight" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                            Malodunajský<br />Vodácky <span className="text-yellow-400">Maratón</span>
                        </span>
                    </div>

                    {/* Desktop Menu */}
                    <div className="hidden lg:flex items-center gap-2">
                        {navItems.map(item => (
                            <NavLink
                                key={item.path}
                                to={item.path}
                                end={item.exact}
                                className={({ isActive }) =>
                                    `px-6 py-2 rounded-sm font-black uppercase tracking-wider text-sm transition-all transform -skew-x-12 border-2 ${isActive
                                        ? item.special ? 'bg-orange-500 text-white border-orange-500' : 'bg-yellow-400 text-blue-900 border-yellow-400'
                                        : item.special ? 'text-orange-400 border-orange-500/30 hover:bg-orange-500 hover:text-white' : 'text-white border-transparent hover:border-white/20 hover:bg-white/10'
                                    }`
                                }
                                style={{ fontFamily: 'Montserrat, sans-serif' }}
                            >
                                <span className="block transform skew-x-12">{item.label}</span>
                            </NavLink>
                        ))}
                        <div className="ml-4 pl-4 border-l border-white/20">
                            <button
                                onClick={() => handleNav('/registracia')}
                                className="font-black uppercase tracking-widest px-6 py-2 shadow-xl transition-all transform -skew-x-12 hover:scale-105 inline-flex items-center gap-2 bg-yellow-400 text-blue-900 hover:bg-yellow-300 text-sm"
                                style={{ fontFamily: 'Montserrat, sans-serif' }}
                            >
                                <span className="transform skew-x-12 flex items-center gap-2">Registrácia <ArrowRight className="w-4 h-4" /></span>
                            </button>
                        </div>
                    </div>

                    {/* Mobile Toggle */}
                    <button
                        className="lg:hidden text-white p-3 rounded-xl border-0 bg-transparent hover:bg-white/10 active:bg-white/20 transition-all flex items-center justify-center"
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        aria-label="Menu"
                    >
                        {mobileMenuOpen ? <X className="w-8 h-8" /> : <Menu className="w-8 h-8" />}
                    </button>
                </div>

                {/* Mobile Menu Overlay */}
                {mobileMenuOpen && (
                    <div className="lg:hidden fixed top-20 inset-x-0 bg-blue-900 border-b border-white/10 shadow-2xl py-6 px-6 flex flex-col gap-3 animate-in fade-in slide-in-from-top-4 duration-300">
                        {navItems.map(item => (
                            <NavLink
                                key={item.path}
                                to={item.path}
                                end={item.exact}
                                onClick={() => setMobileMenuOpen(false)}
                                className={({ isActive }) =>
                                    `p-4 text-left font-black uppercase tracking-widest rounded-2xl transition-all ${isActive
                                        ? item.special ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/20' : 'bg-yellow-400 text-blue-900 shadow-lg shadow-yellow-400/20'
                                        : 'text-white bg-white/5 hover:bg-white/10'
                                    }`
                                }
                            >
                                {item.label}
                            </NavLink>
                        ))}
                        <button
                            onClick={() => handleNav('/registracia')}
                            className="p-4 text-left font-black uppercase tracking-widest rounded-2xl bg-yellow-400 text-blue-900 shadow-lg shadow-yellow-400/20 hover:bg-yellow-300 transition-colors"
                        >
                            Registrácia
                        </button>
                    </div>
                )}
            </nav>

            {/* Hlavný obsah */}
            <main className="flex-grow mt-20 relative">
                <Routes>
                    <Route path="/" element={<PageDomov />} />
                    <Route path="/pravidla" element={<PagePravidla />} />
                    <Route path="/vysledky" element={<PageVysledky />} />
                    <Route path="/galeria" element={<PageGaleria />} />
                    <Route path="/buchta" element={<PageBuchta />} />
                    <Route path="/registracia" element={<PageRegistracia />} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </main>

            {/* Footer */}
            <footer className="bg-blue-950 border-t border-white/10 py-12 relative z-20 mt-auto">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-1 md:md:grid-cols-3 gap-8 items-center text-center md:text-left">
                    <div>
                        <div className="flex items-center justify-center md:justify-start gap-4 mb-4">
                            <img src="https://klubmalydunaj.sk/wp-content/uploads/2020/02/logo-3.png" alt="KMD Logo" className="w-16 h-16 bg-white rounded-full p-1" />
                            <span className="text-white font-black text-xl uppercase tracking-widest" style={{ fontFamily: 'Montserrat, sans-serif' }}>Klub Malý Dunaj</span>
                        </div>
                        <p className="text-white/60 font-medium text-sm">Organizátor Malodunajského vodáckeho maratónu.</p>
                    </div>
                    <div className="flex flex-col gap-2 font-bold text-white/80">
                        <a href="mailto:info@klubmalydunaj.sk" className="text-white/80 no-underline hover:text-yellow-400 transition-colors">info@klubmalydunaj.sk</a>
                        <a href="https://klubmalydunaj.sk" target="_blank" rel="noreferrer" className="text-white/80 no-underline hover:text-yellow-400 transition-colors">www.klubmalydunaj.sk</a>
                    </div>
                    <div className="md:text-right">
                        <div className="bg-yellow-400 text-blue-900 font-black px-6 py-3 rounded-xl shadow-lg inline-block transform -skew-x-12" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                            <span className="block transform skew-x-12">{config.rocnik || '---'} ROČNÍK • {config.datum ? config.datum.split(' ').pop() : '---'}</span>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
}
